import { useEffect, useRef } from "react";
import { useUnsavedChangesWarning } from "../utils/useUnsavedChangesWarning";

/* W-032, 2026-09-30 -- THE ONE PROTECTED FORM DIALOG.
   Four form dialogs closed on ANY click outside them (and none answered Escape), so one stray click threw away
   everything typed. The rule, the same one VendorDashboard's VendorModal already follows:
     - a click on the backdrop closes the dialog ONLY when nothing is unsaved;
     - Escape closes it ONLY when nothing is unsaved (with unsaved changes it does nothing, like the outside click);
     - while something is unsaved, the dialog says so ("Unsaved changes -- click outside won't close.") and the browser
       warns before the page is closed or reloaded (useUnsavedChangesWarning -- the one beforeunload guard, not a second);
     - Cancel / Save / Delete are the adopter's own buttons and are unchanged -- Cancel still discards on purpose.
   Escape first blurs the focused field inside the card, because several fields commit their value on blur; the
   dirty test then reads the state AFTER that commit, so a value typed and not yet committed still counts. */

const DEFAULT_OVERLAY = { position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(8px)",
  display: "flex", alignItems: "center", justifyContent: "center" };

/* `note` (Batch 1b, 2026-11-03): the line shown while something is unsaved -- the default below; null = the dialog shows its own. */
export const GUARDED_NOTE = "Unsaved changes \u2014 click outside won't close.";
export default function GuardedDialog({ isDirty, onClose, cardStyle, overlayStyle, note = GUARDED_NOTE, children }) {
  useUnsavedChangesWarning(isDirty);
  const cardRef = useRef(null);
  const dirtyRef = useRef(isDirty);
  dirtyRef.current = isDirty;
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const onKey = e => {
      if (e.key !== "Escape") return;
      const card = cardRef.current;
      const active = document.activeElement;
      if (card && active && card.contains(active) && typeof active.blur === "function") active.blur();
      setTimeout(() => { if (!dirtyRef.current) closeRef.current(); }, 0);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div data-guarded-backdrop style={overlayStyle || DEFAULT_OVERLAY} onClick={() => { if (!dirtyRef.current) closeRef.current(); }}>
      <div ref={cardRef} data-guarded-card onClick={e => e.stopPropagation()} style={cardStyle}>
        {children}
        {isDirty && note && <div data-guarded-note style={{ fontSize: "11px", color: "#8B8FA3", fontStyle: "italic", textAlign: "right", marginTop: "12px" }}>
          {note}
        </div>}
      </div>
    </div>
  );
}

/* True once the given state differs from what it was when the dialog opened. The snapshot is taken on the FIRST
   render only (a lazy ref), so it is the state the dialog opened with -- the VendorModal idiom, shared. */
export function useSnapshotDirty(state) {
  const snap = useRef(null);
  const now = JSON.stringify(state);
  if (snap.current === null) snap.current = now;
  return now !== snap.current;
}
